import React from 'react'
import styles from 'styles/components/PilotEndedDialog.module.sass'
import { Dialog, DialogContent, IconButton } from '@mui/material'
import { HeartHandshake, X as CloseIcon } from 'lucide-react'
import { useToast } from 'provider/ToastProvider'

/**
 * Microsoft Forms pilot feedback survey. Mirrors the same URL wired into
 * ProfileHeaderSummary's "Give Feedback" button and the workspace's
 * FeedbackButton. A dedicated in-workspace feedback form is planned but not
 * yet built - this is the interim destination.
 */
const PILOT_FEEDBACK_FORM_URL = 'https://forms.cloud.microsoft/r/dpXGJSsRvN'

interface PilotEndedDialogProps {
    isOpen: boolean
    onClose: () => void
}

/**
 * PilotEndedDialog component - shown once when a former Pilot participant's
 * 4-week access window has ended and they attempt to open the workspace.
 * Workspace access is hard-blocked at that point (same as a regular Free
 * user) - this dialog is the one place that transition is explained.
 *
 * @component
 * @param {PilotEndedDialogProps} props The component props.
 * @param {boolean} props.isOpen Indicates whether the dialog is open.
 * @param {() => void} props.onClose Function to call when the dialog is closed.
 *
 * @example
 * <PilotEndedDialog isOpen={isDialogOpen} onClose={() => setIsDialogOpen(false)} />
 */
const PilotEndedDialog: React.FC<PilotEndedDialogProps> = ({ isOpen, onClose }) => {
    const { addToast } = useToast()

    const handleFeedbackClick = () => {
        if (PILOT_FEEDBACK_FORM_URL) {
            window.open(PILOT_FEEDBACK_FORM_URL, '_blank', 'noopener,noreferrer')
            return
        }
        addToast({ type: 'info', message: 'Feedback form coming soon!' })
    }

    return (
        <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogContent className={'sm:max-w-md'}>
                <div className={styles['dialog-header']}>
                    <div className={styles['icon-wrapper']}>
                        <HeartHandshake size={28} color="#7C3AED" />
                    </div>
                    <IconButton
                        onClick={onClose}
                        aria-label='close'
                        className={styles['close-button'] ?? 'close-button'}
                    >
                        <CloseIcon />
                    </IconButton>
                </div>
                <p className={styles['dialog-title']}>Thank you for being part of the Pilot</p>
                <p className={styles['dialog-body']}>
                    Your 4-week Pilot access has wrapped up, and your workspace is now on the
                    Free tier. We&apos;d love to hear{' '}
                    <span
                        className={styles['feedback-link']}
                        role='button'
                        tabIndex={0}
                        onClick={handleFeedbackClick}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleFeedbackClick() }}
                    >
                        what you thought
                    </span>
                    {' '}- and you&apos;re welcome to subscribe any time to keep full access.
                </p>
            </DialogContent>
        </Dialog>
    )
}

export default PilotEndedDialog
